import { Component, inject, input, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { SubSink } from 'subsink';
import { MaintenanceApiService } from '../../apiCalls/maintenance-api.service';
import { CustomSnackbarService } from '../../shared/custom-snackbar.service';

interface RenewalForm {
  renewalType: FormControl<string>;
  serialNumber: FormControl<string>;
  instructions: FormControl<string>;
}

export interface RenewalFormValue {
  serialNumber: string;
  renewalType: string;
  instructions: string;
}

@Component({
  selector: 'app-renewal',
  imports: [MatCardModule, MatButtonModule, MatRadioModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatInputModule, NgxTippyModule, MatIconModule],
  templateUrl: './renewal.component.html',
  styleUrl: './renewal.component.scss',
})
export class RenewalComponent {
  listOfSerialNr = input.required<string[]>();

  private readonly subs = new SubSink();

  private readonly maintenanceApiSvc = inject(MaintenanceApiService);
  private readonly fb = inject(FormBuilder);
  private readonly customSnackbarSvc = inject(CustomSnackbarService);

  /** Reactive signals for state management */
  apiErrorMessage = signal<string>('');
  saveState = signal<boolean>(false);

  renewalYear = new Date().getFullYear() + 1;

  radioOptions = [
    { name: '1 Year Renewal', value: '1 Year' },
    { name: '2 Years Renewal', value: '2 Years' },
    { name: '3 Years Renewal', value: '3 Years' },
    { name: 'Renewal until end of ' + this.renewalYear, value: 'Until end of ' + this.renewalYear },
    { name: 'Custom (specify below)', value: 'Custom' },
  ];

  /** Form definition */
  form: FormGroup<RenewalForm> = this.fb.nonNullable.group({
    renewalType: ['', [Validators.required]],
    serialNumber: [''],
    instructions: [''],
  });

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue: RenewalFormValue = this.form.getRawValue();

    this.saveState.set(true);
    this.clearErrorMessage();

    this.customSnackbarSvc.requestSnackbar();

    this.subs.sink = this.maintenanceApiSvc.requestQuote(formValue).subscribe({
      next: () => this.handleSuccess(),
      error: (error: any) => this.handleError(error),
    });
  }

  /**
   * Handles successful
   */
  private handleSuccess(): void {
    this.clearErrorMessage();
    this.saveState.set(false);
    this.form.reset();
    this.customSnackbarSvc.successSnackbar('Thank you for the maintenance renewal request. A sales representative will contact you within two business days.');
  }

  /**
   * Handles errors
   */
  private handleError(error: any): void {
    const errorMessage = error?.message || 'Unknown error occurred';
    this.apiErrorMessage.set(`: ${errorMessage}`);
    console.error('Error:', errorMessage);
    this.customSnackbarSvc.errorSnackbar();
    this.saveState.set(false);
  }

  /**
   * Clears the API error message
   */
  private clearErrorMessage(): void {
    this.apiErrorMessage.set('');
  }
}
